import {Stack} from '@primer/react'
import {useImperativeHandle, useRef, useState} from 'react'
import {ConversationCommentBox, type ConversationCommentBoxHandle} from '@github-ui/diff-lines/conversation-comment-box'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {useCreatePullRequestTimelineCommentMutation} from '../../mutations/use-create-pull-request-timeline-comment-mutation'
import {useCopilotQuotaGate} from '../toolbar/use-copilot-quota-gate'
import {getCurrentUser} from './utils/get-author-filters'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {Link} from '@github-ui/react-router'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {CompactCommentButton} from '@github-ui/commenting/CompactCommentButton'
import {flushSync} from 'react-dom'
import {issueCommentAnchor} from './utils/comment-anchors'

export interface IssueCommentComposerHandle {
  /** Inserts quoted text into the draft, appending it to any existing draft when `append` is set. */
  insertQuote: (quotedText: string, options?: {append?: boolean}) => void
}

export interface IssueCommentComposerProps {
  /** Renders nothing while true, but keeps the in-progress draft. */
  collapsed: boolean
  /** Called when the component requests to be collapsed or expanded. */
  onCollapsedChange: (collapsed: boolean) => void
  ref?: React.Ref<IssueCommentComposerHandle>
}

function CollapsedCommentComposer({onClick}: {onClick: () => void}) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const currentUser = getCurrentUser()

  return (
    <Stack gap="condensed" direction="horizontal" align="center">
      {currentUser && (
        <Link to={`/${currentUser.login}`} {...getHovercardAttributesForType('user', {login: currentUser.login})}>
          <GitHubAvatar size={32} src={currentUser.avatarUrl} alt="Your profile" />
        </Link>
      )}
      <CompactCommentButton onClick={onClick}>Add a comment</CompactCommentButton>
    </Stack>
  )
}

/**
 * Composer for a new pull request timeline (conversation) comment. Owns the draft state so it
 * can be dropped in anywhere conversation comments are rendered.
 */
export function IssueCommentComposer({collapsed = false, ref, onCollapsedChange}: IssueCommentComposerProps) {
  const [commentText, setCommentText] = useState('')
  const {mutate: createTimelineComment, isPending} = useCreatePullRequestTimelineCommentMutation()

  // The draft is retained while collapsed, but the quota gate should stay idle until it is
  // visible again — otherwise it would fetch quota data for a composer nobody can see.
  const {banner: quotaBanner, submitDisabled: quotaSubmitDisabled} = useCopilotQuotaGate(collapsed ? '' : commentText)

  const onSubmit = () => {
    if (quotaSubmitDisabled || isPending || commentText.trim().length === 0) return

    createTimelineComment(
      {text: commentText},
      {
        onSuccess: data => {
          onCollapsedChange?.(true)
          setCommentText('')
          requestAnimationFrame(() => {
            ssrSafeDocument
              ?.getElementById(issueCommentAnchor(data.issueComment.id))
              ?.scrollIntoView({behavior: 'smooth'})
          })
        },
      },
    )
  }

  const onCancel = () => {
    setCommentText('')
    onCollapsedChange?.(true)
  }

  const onExpand = () => {
    // eslint-disable-next-line @eslint-react/dom-no-flush-sync
    flushSync(() => onCollapsedChange?.(false))
    commentBoxRef.current?.focus()
  }

  const commentBoxRef = useRef<ConversationCommentBoxHandle>(null)

  useImperativeHandle(
    ref,
    () => ({
      insertQuote(quotedText, {append = false} = {}) {
        // eslint-disable-next-line @eslint-react/dom-no-flush-sync
        flushSync(() => {
          onCollapsedChange?.(false)
          setCommentText(prev => (append && prev.trim().length > 0 ? `${prev}\n\n${quotedText}` : quotedText))
        })

        commentBoxRef.current?.focus()
        commentBoxRef.current?.moveCursorToEnd()
      },
    }),
    [onCollapsedChange],
  )

  if (collapsed) return <CollapsedCommentComposer onClick={onExpand} />

  return (
    <>
      {quotaBanner}
      <ConversationCommentBox
        ref={commentBoxRef}
        label="Add a comment"
        value={commentText}
        onChange={setCommentText}
        onSave={onSubmit}
        validationResult={{isValid: true}}
        onCancel={onCancel}
        disabled={isPending}
        saveButtonText="Comment"
        maxHeightLines={15}
      />
    </>
  )
}

/**
 * Whether the conversation comment composer should be offered: only in conversation view,
 * and only when the current user is allowed to comment.
 */
export function isIssueCommentComposerEnabled(isReviewCommentsMode: boolean, currentUserCanComment: boolean): boolean {
  return !isReviewCommentsMode && currentUserCanComment
}
